import { useEffect, useState } from 'react';
import { fetchRemoteSource, RemoteFetchError } from './fetchRemote.ts';
import { decodeText, parseLocationHash, ShareDecodeError } from './hash.ts';

/**
 * Where the initial source was loaded from. Stored as provenance so the
 * toolbar's Share group (A-PR2) can decide whether to emit `#url=` or
 * `#text=` for Copy share link.
 */
export type LoadedFrom =
    | { kind: 'url'; url: string; body: string }
    | { kind: 'text' }
    | { kind: 'none' };

export interface InitialSourceState {
    /** True while a `#url=` fetch is in flight. */
    isLoading: boolean;
    /** Decoded / fetched source text; null while loading or on error. */
    source: string | null;
    loadedFrom: LoadedFrom;
    loadError: ShareDecodeError | RemoteFetchError | null;
}

/**
 * One-shot hook: reads `location.hash` on mount and resolves the initial
 * editor source. Never re-fires on subsequent hash changes.
 *
 * Path-independent: reads only `location.hash`, never `location.pathname`,
 * so it resolves identically at `/open#…` and bare `/#…`.
 *
 * StrictMode-safe: the async `#url=` branch uses a `cancelled` guard
 * (same pattern as `PreviewPane.tsx`) so double-invoke in development
 * does not produce a stale state update.
 */
export function useInitialSource(): InitialSourceState {
    const [state, setState] = useState<InitialSourceState>(() => {
        const parsed = parseLocationHash(location.hash);
        if (parsed.kind === 'text') {
            try {
                const source = decodeText(parsed.payload);
                return {
                    isLoading: false,
                    source,
                    loadedFrom: { kind: 'text' },
                    loadError: null,
                };
            } catch (err) {
                return {
                    isLoading: false,
                    source: null,
                    loadedFrom: { kind: 'none' },
                    loadError:
                        err instanceof ShareDecodeError
                            ? err
                            : new ShareDecodeError('Unexpected decode error', err),
                };
            }
        }
        if (parsed.kind === 'url') {
            return {
                isLoading: true,
                source: null,
                loadedFrom: { kind: 'none' },
                loadError: null,
            };
        }
        return { isLoading: false, source: null, loadedFrom: { kind: 'none' }, loadError: null };
    });

    // Async path: fetch the remote source when the hash at mount is `#url=`.
    // Runs once on mount. The `cancelled` guard makes double-invoke in
    // StrictMode harmless — the cleanup fires before the re-mount effect.
    useEffect(() => {
        const parsed = parseLocationHash(location.hash);
        if (parsed.kind !== 'url') return;
        let cancelled = false;
        void fetchRemoteSource(parsed.url).then(
            (body) => {
                if (cancelled) return;
                setState({
                    isLoading: false,
                    source: body,
                    loadedFrom: { kind: 'url', url: parsed.url, body },
                    loadError: null,
                });
            },
            (err: unknown) => {
                if (cancelled) return;
                setState({
                    isLoading: false,
                    source: null,
                    loadedFrom: { kind: 'none' },
                    loadError:
                        err instanceof RemoteFetchError
                            ? err
                            : new RemoteFetchError('cors', 'Unexpected fetch error', {
                                  cause: err,
                              }),
                });
            },
        );
        return () => {
            cancelled = true;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return state;
}
